// ================================================================
// 📦 PRODUCTS REPORT - Relatório de Produtos
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import { useChartsColors } from '../../hooks/useChartsColors';
import '../../components/reports/Reports.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

const ProductsReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productsData, setProductsData] = useState(null);
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const chartsColors = useChartsColors();

  const loadProductsReportWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('📦 Carregando Relatório de Produtos com filtros...', filtersToUse);

      const result = await ReportsService.getProductsReport({
        period: filtersToUse.period,
        product_id: filtersToUse.advancedFilters?.product_id
      });

      if (result.success) {
        setProductsData(result.data);
        console.log('✅ Relatório de Produtos carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProductsReport = useCallback(async () => {
    loadProductsReportWithFilters(filters);
  }, [filters, loadProductsReportWithFilters]);

  useEffect(() => {
    loadProductsReport();
  }, [loadProductsReport]);

  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => {
      const newFilters = { ...prev, period: newPeriod };
      setTimeout(() => loadProductsReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = { ...prev, advancedFilters };
      setTimeout(() => loadProductsReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const topProductsOptions = { 
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      x: {
        grid: { 
          color: chartsColors 
        }
      },
      y: { 
        grid: {
          color: chartsColors 
        }     
      }
    } 
  }


  const productsByCategoryData = productsData?.charts?.productsByCategory ? {
    labels: productsData.charts.productsByCategory.map(item => item.category),
    datasets: [{
      label: 'Receita por Categoria',
      data: productsData.charts.productsByCategory.map(item => item.total_revenue / 100),
      backgroundColor: ['#007bff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#17a2b8', '#fd7e14', '#20c997'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  } : { labels: [], datasets: [] };

  const topProductsData = productsData?.charts?.topProducts ? {
    labels: productsData.charts.topProducts.map(item => item.product_name?.substring(0, 20) + '...'),
    datasets: [{
      label: 'Vendas',
      data: productsData.charts.topProducts.map(item => item.sales_count),
      backgroundColor: '#007bff',
      borderWidth: 0
    }]
  } : { labels: [], datasets: [] };

  const productsColumns = [
    {
      key: 'product_name',
      label: 'Produto',
      sortable: true,
      render: (value, row) => (
        <div>
          <strong>{value}</strong>
          <br />
          <small style={{ color: '#6c757d' }}>{row.product_code}</small>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Categoria',
      sortable: true
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          backgroundColor: value === 'active' ? '#d4edda' : '#f8d7da',
          color: value === 'active' ? '#155724' : '#721c24'
        }}>
          {value === 'active' ? 'Ativo' : 'Inativo'}
        </span>
      )
    },
    {
      key: 'sales_count',
      label: 'Vendas',
      sortable: true
    },
    {
      key: 'revenue_formatted',
      label: 'Receita',
      sortable: true
    },
    {
      key: 'avg_price_formatted',
      label: 'Preço Médio',
      sortable: true
    },
    {
      key: 'affiliates_count',
      label: 'Afiliados',
      sortable: true
    }
  ];

  return (
    <MasterLayout>
      <Container fluid className="reports-container" style={{ padding: '24px', maxWidth: '100%' }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon  icon="mdi:package-variant" style={{ fontSize: '22px', color: '#007bff' }} />
                <span className='text-contrast' style={{ fontSize: '22px', fontWeight: '700', display: 'block' }}>
                  Relatório de Produtos
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button
                  variant="outline-primary"
                  onClick={() => setShowFilterDrawer(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:filter-variant" style={{ fontSize: '20px' }} />
                  Filtros Avançados
                  {Object.keys(filters.advancedFilters).length > 0 && (
                    <span style={{
                      backgroundColor: '#007bff',
                      color: '#fff',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      {Object.keys(filters.advancedFilters).length}
                    </span>
                  )}
                </Button>
                <Button
                  variant="outline-success"
                  onClick={() => console.log('Exportar CSV')}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:file-excel" style={{ fontSize: '20px' }} />
                  Exportar CSV
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={loadProductsReport}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                >
                  <Icon icon="mdi:refresh" style={{ fontSize: '20px' }} />
                  Atualizar
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filter Bar */}
        <FilterBar
          period={filters.period}
          onPeriodChange={handlePeriodChange}
          onApplyAdvancedFilters={handleAdvancedFilters}
          onRefresh={loadProductsReport}
          loading={loading}
          advancedFilters={filters.advancedFilters}
          onUserFilterChange={() => {}}
        />

        <hr style={{ margin: '24px 0', borderTop: '2px solid #dee2e6' }} />

        {/* Error State */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p style={{ marginTop: '16px', color: '#6c757d' }}>Carregando dados dos produtos...</p>
          </div>
        )}

        {/* Main Content */}
        {!loading && productsData && (
          <>
            {/* KPIs */}
            <Row className="mb-4">
              <Col xs={12} sm={6} lg={4} xl={2} className="mb-3">
                <StatCard
                  title={productsData.kpis.total_products.label}
                  value={productsData.kpis.total_products.value}
                  icon="mdi:package-variant"
                  color="#007bff"
                />
              </Col>
              <Col xs={12} sm={6} lg={4} xl={2} className="mb-3">
                <StatCard
                  title={productsData.kpis.active_products.label}
                  value={productsData.kpis.active_products.value}
                  icon="mdi:check-circle"
                  color="#28a745"
                />
              </Col>
              <Col xs={12} sm={6} lg={4} xl={2} className="mb-3">
                <StatCard
                  title={productsData.kpis.total_sales.label}
                  value={productsData.kpis.total_sales.value}
                  icon="mdi:cart-check"
                  color="#17a2b8"
                />
              </Col>
              <Col xs={12} sm={6} lg={4} xl={3} className="mb-3">
                <StatCard
                  title={productsData.kpis.total_revenue.label}
                  value={productsData.kpis.total_revenue.formatted}
                  icon="mdi:currency-usd"
                  color="#ffc107"
                />
              </Col>
              <Col xs={12} sm={6} lg={4} xl={3} className="mb-3">
                <StatCard
                  title={productsData.kpis.avg_product_value.label}
                  value={productsData.kpis.avg_product_value.formatted}
                  icon="mdi:chart-line"
                  color="#6f42c1"
                />
              </Col>
            </Row>

            {/* Charts */}
            <Row className="mb-4">
              <Col md={6} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Top 10 Produtos
                    </span>
                    <Bar data={topProductsData} options={topProductsOptions} />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Receita por Categoria
                    </span>
                    <Doughnut data={productsByCategoryData} options={{ responsive: true, maintainAspectRatio: true }} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Products Table */}
            <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Card.Body>
                <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                  Lista de Produtos
                </span>
                <SmartTable
                  columns={productsColumns}
                  data={productsData.productsList || []}
                  searchable={true}
                  pagination={true}
                  itemsPerPage={10}
                />
              </Card.Body>
            </Card>
          </>
        )}

        {/* Filter Drawer */}
        <FilterDrawer
          show={showFilterDrawer}
          onHide={() => setShowFilterDrawer(false)}
          onApply={(filters) => {
            setShowFilterDrawer(false);
            handleAdvancedFilters(filters);
          }}
          currentFilters={filters.advancedFilters}
        />
      </Container>
    </MasterLayout>
  );
};

export default ProductsReport;

